<template>
  <v-flex layout row>
    <v-flex layout column>
      <v-flex class="LocationPicker__map">
        <l-map ref="map" 
          :style="{
            height: parent === 'managePath' ? '40vh' : '80vh'
          }" 
          :options="{ zoomControl: false }">
          <!-- background map -->
          <l-tile-layer :url="selectedUrl" :attribution="attribution" />
          <!-- scale -->
          <l-control-scale
            position="bottomright"
            :imperial="false"
          ></l-control-scale>
          <!-- zoom -->
          <l-control-zoom position="topright"></l-control-zoom>
          <!-- settings -->
          <l-control position="topleft" v-if="parent !== 'settingsBar'">
            <v-hover v-model="optionsHover">
              <v-card
                class="optionsCard"
                color="rgb(255,255,255,0.9)"
                ref="menu"
              >
                <v-flex column align-center pr-2 pl-2>
                  <v-menu
                    open-on-hover
                    bottom
                    :offset-x="true"
                    :min-width="100"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-flex
                        layout
                        row
                        justify-center
                        align-center
                        pt-3
                        v-on="on"
                        v-bind="attrs"
                      >
                        <span class="legendText nonClickable">
                          {{ $t('locationPicker.mapLayer') }}
                        </span>
                        <v-img :src="layerUrl" class="layerIcon" contain>
                        </v-img>
                      </v-flex>
                    </template>
                    <v-list>
                      <v-list-tile
                        v-for="layer in layers"
                        :key="layer.type"
                        @click="changeLayer(layer)"
                        align="center"
                        justify="center"
                      >
                        <v-flex layout row align-center justify-center>
                          <v-icon
                            size="20"
                            :color="
                              selectedUrl === layer.url ? 'primary' : 'grey'
                            "
                          >
                            {{ layer.icon }}
                          </v-icon>
                          <v-card-text>
                            {{ $t('locationPicker.' + layer.type) }}
                          </v-card-text>
                        </v-flex>
                      </v-list-tile>
                    </v-list>
                  </v-menu>

                  <v-tooltip right>
                    <template v-slot:activator="{ on, attrs }">
                      <v-flex
                        layout
                        row
                        justify-center
                        pt-4
                        v-on="on"
                        v-bind="attrs"
                      >
                        <span class="legendText nonClickable">
                          {{ $t('locationPicker.localiseMe') }}
                        </span>
                        <v-img
                          :src="iconLocationCenter"
                          class="locationFindIcon"
                          contain
                          v-on="on"
                          @click="locateUser()"
                        >
                        </v-img>
                      </v-flex>
                    </template>
                    <span>
                      {{ $t('locationPicker.findMyLocation') }}
                    </span>
                  </v-tooltip>
                </v-flex>
              </v-card>
            </v-hover>
          </l-control>

          <!-- markers -->
          <l-control
            position="bottomleft"
            :disableClickPropagation="true"
            :disableScrollPropagation="true"
            v-if="
              !(
                parent === 'settingsBar' &&
                !pathGeolocation &&
                missionsGeolocations.length < 1
              )
            "
          >
            <v-hover v-model="dataHover">
              <v-card
                :class="
                  parent === 'settingsBar' ? 'legendCardBar' : 'legendCard'
                "
                color="rgb(255,255,255,0.9)"
              >
                <v-flex column align-center pr-2 pl-2>
                  <v-tooltip right>
                    <template v-slot:activator="{ on }">
                      <v-flex
                        layout
                        row
                        justify-center
                        align-center
                        pt-2
                        pb-2
                        v-on="on"
                        class="pointerClass"
                        v-if="parent === 'pathForm'"
                        @click="focusOnMarker()"
                      >
                        <span class="legendText">
                          {{ $t('locationPicker.pathLocation') }}
                        </span>
                        <v-img max-height="30" contain :src="iconStartFlag" />
                      </v-flex>
                    </template>
                    <span v-if="marker">
                      {{ $t('locationPicker.centerPath') }}
                    </span>
                    <span v-else>
                      {{ $t('locationPicker.noLocationNow') }}
                    </span>
                  </v-tooltip>
                  <v-tooltip right>
                    <template v-slot:activator="{ on }">
                      <v-flex
                        layout
                        row
                        justify-center
                        align-center
                        pt-2
                        pb-2
                        v-on="on"
                        class="pointerClass"
                        v-if="parent === 'missionCard'"
                        @click="focusOnMarker()"
                      >
                        <span class="legendText">
                          {{ $t('locationPicker.missionLocation') }}
                        </span>
                        <v-icon color="primary"> mdi-circle-outline </v-icon>
                      </v-flex>
                    </template>
                    <span v-if="marker">
                      {{ $t('locationPicker.centerMission') }}
                    </span>
                    <span v-else>
                      {{ $t('locationPicker.noLocationNow') }}
                    </span>
                  </v-tooltip>
                  <v-flex layout column v-if="parent === 'settingsBar'" pb-2>
                    <v-tooltip right>
                      <template v-slot:activator="{ on }">
                        <v-flex
                          layout
                          row
                          justify-center
                          align-center
                          pt-2
                          pb-2
                          class="pointerClass"
                          v-on="on"
                          @click="focusOn(pathGeolocation, 'path', 0)"
                        >
                          <span class="legendTextlarge mr-2">
                            {{ $t('locationPicker.pathEntry') }}
                          </span>
                          <v-img max-height="30" contain :src="iconStartFlag" />
                        </v-flex>
                      </template>
                      <span v-if="pathGeolocation">
                        {{ $t('locationPicker.centerEntry') }}
                      </span>
                      <span v-else>
                        {{ $t('locationPicker.noLocationNow') }}
                      </span>
                    </v-tooltip>
                    <v-divider />
                    <v-tooltip right>
                      <template v-slot:activator="{ on }">
                        <v-flex
                          layout
                          row
                          justify-center
                          align-center
                          pt-2
                          pb-2
                          v-on="on"
                        >
                          <span class="legendTextlarge mr-3">
                            {{ $t('locationPicker.localisedMissions') }}
                          </span>
                          <v-icon color="primary"> mdi-circle-outline </v-icon>
                        </v-flex>
                      </template>
                      <span v-if="missionsGeolocations.length > 0">
                        {{ $t('locationPicker.centerMissions') }}
                      </span>
                      <span v-else>
                        {{ $t('locationPicker.noLocationMissions') }}
                      </span>
                    </v-tooltip>
                    <v-flex
                      layout
                      justify-left
                      align-center
                      pl-2
                      pt-1
                      pb-1
                      class="pointerClass"
                      v-for="(mission, index) in missionsGeolocations"
                      :key="index"
                      @click="
                        focusOn(mission.missionGeolocation, 'mission', index)
                      "
                    >
                      <v-switch
                        v-if="isRealtime"
                        color="primary"
                        class="switchHistory"
                        v-model="missionsActive"
                        :value="mission.name"
                        :label="index + 1 + '. ' + mission.name"
                      >
                      </v-switch>
                      <span v-else> {{ index + 1 }}. {{ mission.name }} </span>
                    </v-flex>
                  </v-flex>
                </v-flex>
              </v-card>
            </v-hover>
          </l-control>
        </l-map>

      </v-flex>
      
      <!-- actions -->
      <v-flex layout row pl-2 pr-2
        v-if="parent !== 'managePath'">
        <v-btn color="primary" outline class="text-none" @click="cancel">
          {{ $t('locationPicker.cancel') }}
        </v-btn>
        <v-btn
          v-if="parent === 'missionCard'"
          color="red"
          class="text-none white--text"
          :disabled="!marker"
          @click="deleteLocation()"
        >
          {{ $t('locationPicker.delete') }}
        </v-btn>
        <v-spacer />
        <v-slider 
          v-if="parent === 'pathForm'"
          v-model="site.radius"
          color="#009999"
          :min="50"
          :max="1000"
          :step="50"
          :label="$t('locationPicker.radiusLabel')"
        >
          <template v-slot:append>
            <v-text-field style="margin-top: -4px;padding-top: 0px !important"
              v-model="site.radius"
              readonly
            ></v-text-field>
        </template>
        </v-slider>
        <v-spacer />
        <v-btn
          v-if="parent !== 'settingsBar'"
          color="primary"
          class="text-none"
          :disabled="!marker"
          @click="submit"
        >
          {{ $t('locationPicker.submitLabel') }}
        </v-btn>
      </v-flex>
    </v-flex>
    <v-card
      v-if="
        parent === 'settingsBar' &&
        !pathGeolocation &&
        missionsGeolocations.length < 1
      "
      class="banner"
      color="rgba(230,230,230,0.3)"
    >
      <v-flex layout column align-center justify-center>
        <span>
          {{ $t('locationPicker.noLocation') }}
        </span>
        <span>
          {{ $t('locationPicker.addLocation') }}
        </span>
      </v-flex>
    </v-card>
  </v-flex>
  <!--v-btn v-if="parent!=='settingsBar'"
        outline
        color="red"
        :disabled="!circle" 
        @click="deleteLocation">
          Supprimer
        </v-btn-->
</template>

<script>
import {
  LMap,
  LTileLayer,
  LControlScale,
  LControl,
  LControlZoom,
} from 'vue2-leaflet';
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch';
//import LGeosearch from 'vue2-leaflet-geosearch';
import L, { Icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-geosearch/dist/geosearch.css';

export default {
  name: 'LocationPicker',
  components: {
    LMap,
    LTileLayer,
    LControlScale,
    LControlZoom,
    LControl,
    //LGeosearch
  },
  props: {
    latCenter: {
      type: Number,
      default: 46.85,
    },
    lngCenter: {
      type: Number,
      default: 2.35,
    },
    zoomOnClick: {
      type: Number,
      default: 16,
    },
    iconLocationCenter: {
      type: String,
      default: '/images/location-center.png',
    },
    iconStartFlag: {
      type: String,
      default: '/images/marker-flag.svg',
    },
    iconUrl: {
      type: String,
      default: require('leaflet/dist/images/marker-icon.png'),
    },
    iconRetinaUrl: {
      type: String,
      default: require('leaflet/dist/images/marker-icon-2x.png'),
    },
    layerUrl: {
      type: String,
      default: require('leaflet/dist/images/layers-2x.png'),
    },
    shadowUrl: {},
    clearAfterSubmit: {
      type: Boolean,
      default: true,
    },
    defaultLocation: {
      type: Object,
      default: null,
    },
    defaultRadius: {
      type: Number,
      required: true,
    },
    color: {
      type: String,
      default: 'white',
    },
    parent: {
      type: String,
      default: 'pathForm',
    },
    missionsGeolocations: {
      type: Array,
      default: null,
    },
    pathGeolocation: {
      type: Object,
      default: null,
    },
    pathTrace: {
      type: Object,
      default: null,
    },
    isRealtime: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      optionsHover: false,
      dataHover: false,
      map: null,
      zoomFrance: 5,
      latDefault: 46.85,
      lngDefault: 2.35,
      site: {
        latlng: null,
        radius: this.defaultRadius,
      },
      marker: null,
      circle: null,
      polyline: null,
      markers: {},
      missionsActive: [],
      textFieldKey: 0,
      layers: [
        {
          type: 'plan',
          url: 'https://mt1.google.com/vt/lyrs=r&x={x}&y={y}&z={z}',
          attribution: '&copy; 2018 Google',
          icon: 'mdi-google-maps',
        },
        {
          type: 'satellite',
          url: 'https://mt1.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',
          attribution: '&copy; 2018 Google',
          icon: 'mdi-earth',
        },
        {
          type: 'carte',
          url: 'https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
          attribution:
            'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>',
          icon: 'mdi-compass-outline',
        },
      ],
      selectedUrl: '',
      provider: new OpenStreetMapProvider(),
      playerCount: 0,
    };
  },
  created() {
    if (this.parent === 'settingsBar') {
      this.selectedUrl =
        'https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png';
      this.attribution =
        'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>';
    } else {
      this.selectedUrl = 'https://mt1.google.com/vt/lyrs=r&x={x}&y={y}&z={z}';
      this.attribution = '&copy; 2022 Google';
    }

    delete Icon.Default.prototype._getIconUrl;

    Icon.Default.mergeOptions({
      iconRetinaUrl: this.iconStartFlag,
      iconUrl: this.iconStartFlag,
      shadowUrl: this.shadowUrl,
    });
  },
  mounted() {
    this.map = this.$refs.map.mapObject;

    if (this.parent !== 'settingsBar') {
      if (this.parent === 'pathForm') {
        this.map.on('click', this.createFlag);
        this.map.on('click', this.createPathCircle);
      } else {
        this.map.on('click', this.createCircle);
      }
    }

    this.initMap(this.defaultLocation);

    if (this.parent === 'pathForm' || this.parent === 'missionCard') {
      this.addGeosearchControl();
   

    }
  },
  computed: {
    updatedPlayer() {
      return this.$store.state.players.updatedPlayer;
    },
    updatedPlayerMarker() {
      if (!this.updatedPlayer) return null;
      return {
        coordinates: {
          lat: this.updatedPlayer.geolocation.coordinates[1],
          lng: this.updatedPlayer.geolocation.coordinates[0],
        },
        type: 'player',
        name: this.updatedPlayer.username,
        fillOpacity: 0.3,
        fillColor: 'white',
        color: '#009999',
        radius: 5,
      };
    },
    missionMarkers() {
      return this.missionsGeolocations.map((mission) => {
        return {
          coordinates: {
            lat: mission.missionGeolocation.coordinates[1],
            lng: mission.missionGeolocation.coordinates[0],
          },
          type: 'mission',
          name: mission.name,
          color: '#009999',
          fillColor: mission.isActive ? '#009999' : 'white',
          fillOpacity: mission.isActive ? 0.6 : 0.3,
          radius: 10,
        };
      });
    },
    invertedTraceCoordinates() {
      return this.pathTrace.latlngs.map(inner =>
        inner.map(([lon, lat]) => [lat, lon])
      );
    }
  },
  watch: {
    selectedUrl(newValue) {
      return newValue;
    },
    attribution(newValue) {
      return newValue;
    },
    defaultLocation(newValue, oldValue) {
      if (this.parent !== 'managePath') return;
      this.initMap(this.defaultLocation);
    },
    'site.radius'(newValue) {
      this.createPathCircle({
        radius:newValue,
        latlng:{
          lat: this.site.latlng.lat,
          lng: this.site.latlng.lng,
        }        
      });
    },

    updatedPlayerMarker(newValue, oldValue) {
      if (this.parent !== 'settingsBar') return;
      //newValue.color = this.playerMarkers.find(p=>p.name == newValue.name).color
      if (this.markers[newValue.name]) {
        newValue.color = this.markers[newValue.name].options.color;
        this.map.removeLayer(this.markers[newValue.name]);
      } else {
        newValue.color = this.getRandomColor();
        this.playerCount += 1;
        this.$toast.show(
          `New Player ${newValue.name}, total: ${this.playerCount}`,
          '',
          { color: newValue.color }
        );
      }
      this.addMarkers([newValue]);
    },
    missionsActive(activeMissionsList, oldActiveMissionsList) {
      console.log(
        'watch missionactive',
        activeMissionsList,
        activeMissionsList.length,
        oldActiveMissionsList,
        oldActiveMissionsList.length
      );
      //if (activeMissionsList.length <=0) return

      let missionName;
      if (
        activeMissionsList.length === oldActiveMissionsList.length ||
        Math.abs(activeMissionsList.length - oldActiveMissionsList.length) > 1
      ) {
        // no change or init
        return;
      } else if (activeMissionsList.length > oldActiveMissionsList.length) {
        // add active mission
        missionName = this.arraydiff(
          activeMissionsList,
          oldActiveMissionsList
        )[0];
      } else if (activeMissionsList.length < oldActiveMissionsList.length) {
        // remove active mission
        missionName = this.arraydiff(
          oldActiveMissionsList,
          activeMissionsList
        )[0];
      }
      console.log('missionName', missionName);

      // update path missions, BEFORE change marker
      this.$emit('switch-active', activeMissionsList);

      if (missionName.length <= 0) return;

      let oldMarker = this.markers[missionName];
      let newMarker = this.missionMarkers.find((m) => m.name === missionName);
      this.map.removeLayer(this.markers[missionName]);

      // Invert marker fill
      newMarker.fillColor =
        oldMarker.options.fillColor === 'white' ? '#009999' : 'white';
      newMarker.fillOpacity = oldMarker.options.fillOpacity === 0.3 ? 0.6 : 0.3;
      this.addMarkers([newMarker]);
    },
  },
  methods: {
    arraydiff(arr1, arr2) {
      return [...new Set(arr1.filter((el) => !arr2.includes(el)))];
    },
    getRandomColor() {
      const letters = '0123456789ABCDEF';
      let color = '#';

      for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
      }

      return color;
    },
    initMap() {
      this.clear();

      if (this.parent === 'pathForm') {
        if (this.defaultLocation) {
          // si déjà une localisation au parcours
          
          this.createPathCircle({
            radius:this.defaultRadius,
            latlng: {
              lat: this.defaultLocation.coordinates[1],
              lng: this.defaultLocation.coordinates[0],
            },
          });
          this.createFlag({
            latlng: {
              lat: this.defaultLocation.coordinates[1],
              lng: this.defaultLocation.coordinates[0],
            },
          });
        } else {
          // si pas de localisation au parcours
          this.map.setView(
            new L.LatLng(this.latCenter, this.lngCenter),
            this.zoomFrance
          );
        }
      }
      if (this.parent === 'managePath') {
        if (this.pathTrace) {
          // si déjà une trace au parcours
          const trace = {
            latlngs: this.invertedTraceCoordinates,
            properties: this.pathTrace.properties,
          }
          // si déjà une trace au parcours
          this.createLine(trace)//this.pathTrace)//this.invertedTraceCoordinates)
        } else {
          // si pas de localisation au parcours
          this.map.setView(
            new L.LatLng(this.latCenter, this.lngCenter),
            this.zoomFrance
          );
        }
      }
      if (this.parent === 'missionCard') {
        if (this.defaultLocation) {
          // si déjà une localisation à la mission
          this.createCircle({
            latlng: {
              lat: this.defaultLocation.coordinates[1],
              lng: this.defaultLocation.coordinates[0],
            },
          });
        } else if (
          this.latCenter !== this.latDefault ||
          this.lngCenter !== this.lngDefault
        ) {
          // s'il y a une localisation au parcours de la mission
          this.map.setView(
            new L.LatLng(this.latCenter, this.lngCenter),
            this.zoomOnClick - 2
          );
        } else if (
          this.latCenter === this.latDefault ||
          this.lngCenter === this.lngDefault
        ) {
          // si pas de localisation au parcours non plus
          this.map.setView(
            new L.LatLng(this.latCenter, this.lngCenter),
            this.zoomFrance
          );
        }
      }
      if (this.parent === 'settingsBar') {
        let pathMarkers = [];
        if (this.missionsGeolocations && this.missionsGeolocations.length > 0) {
          // cercles des missions
          this.missionsActive = this.missionsGeolocations
            .filter((m) => m.isActive)
            .map((m) => m.name);

          if (this.missionsActive.length === 1) {
            let newMarker;
            // Specific init marker fill for 1 unique active mission, marker updated in watch
            this.missionMarkers.forEach((marker) => {
              newMarker = new L.Circle(marker.coordinates, {
                radius: marker.radius,
                fillColor: 'white',
                color: marker.color,
                weight: 4,
                fillOpacity: 0.3,
              })
                .addTo(this.map)
                .bindPopup(marker.name, { closeButton: true });
              this.$set(this.markers, marker.name, newMarker);
            });
          } else {
            this.addMarkers(this.missionMarkers);
          }
        }

        if (this.pathGeolocation) {
          //drapeau du début de parcours
          pathMarkers[0] = {
            coordinates: {
              lat: this.pathGeolocation.coordinates[1],
              lng: this.pathGeolocation.coordinates[0],
            },
            type: 'flag',
            name: 'parcours',
            color: 'orange',
            fillColor: 'transparent',
            radius: 1000,
          };
          this.addMarkers(pathMarkers);
        }
        if (this.pathTrace) {
          const trace = {
            latlngs: this.invertedTraceCoordinates,
            properties: this.pathTrace.properties,
          }
          // si déjà une trace au parcours
          this.createLine(trace)//this.pathTrace)//this.invertedTraceCoordinates)
          
        }
        this.map.setView(
          {
            lat: this.pathGeolocation.coordinates[1],
            lng: this.pathGeolocation.coordinates[0],
          },
          this.zoomOnClick
        );
      }
    },
    addGeosearchControl() {
      if (!this.$refs.map._isMounted) {
        return;
      }

      const searchControl = new GeoSearchControl({
        provider: new OpenStreetMapProvider(),
        showMarker: false,
        style: 'bar',
        searchLabel: this.$t('locationPicker.searchLabel'),
      });
      this.map.addControl(searchControl);
      this.map.on('geosearch/showlocation',(result) => {
        console.log("map results", result.marker, result.location)
        //const latLng= new L.LatLng(result.location.x, result.location.y);
        //const marker = new L.Marker (latLng);
        if (this.parent === 'pathForm') {
          this.createFlag({
            latlng: {
              lat: result.location.y,
              lng: result.location.x,
            },
          });
        } else if (this.parent === 'missionCard') {
          this.createCircle({
            latlng: {
              lat: result.location.y,
              lng: result.location.x,
            },
          });
        }
      })
    },
    createPathCircle(e) {
      if (!this.optionsHover && !this.dataHover) {
        // si la personne clique sur la carte mais pas sur les menu => car les prop disableClickPropagation et disableScrollPropagation ne marchent pas
        if (this.circle) {
          this.map.removeLayer(this.circle);
        }
        this.circle = new L.Circle(e.latlng, {
          radius: e.radius || this.site.radius,
          color: '#009999',
          fillOpacity: 0,
          dashArray:4,
          weight: 2,
        }).addTo(this.map);
        this.map.setView(
          e.latlng,
          Math.max(this.map.getZoom(), this.zoomOnClick)
        );
      }
    },
    createCircle(e) {
      if (!this.optionsHover && !this.dataHover) {
        // si la personne clique sur la carte mais pas sur les menu => car les prop disableClickPropagation et disableScrollPropagation ne marchent pas
        if (this.marker) {
          this.map.removeLayer(this.marker);
        }
        this.marker = new L.Circle(e.latlng, {
          fillColor: 'white',
          color: '#009999',
          weight: 4,
          fillOpacity: 0.7,
        }).addTo(this.map);
        
        this.site.latlng = e.latlng
        this.map.setView(
          e.latlng,
          Math.max(this.map.getZoom(), this.zoomOnClick)
        );
      }
    },

    createFlag(e) {
      if (!this.optionsHover && !this.dataHover) {
        // si la personne clique sur la carte mais pas sur les menu => car les prop disableClickPropagation et disableScrollPropagation ne marchent pas
        if (this.marker) {
          this.map.removeLayer(this.marker);
        }
        /*const myFlag=L.icon({
          iconUrl: this.iconStartFlag,
          iconSize: [38, 40],
          popupAnchor: [0, -20]
        });*/
        this.marker = new L.Marker(e.latlng /*, {icon: myFlag }*/).addTo(
          this.map
        );
        this.site.latlng = e.latlng
        this.map.setView(
          e.latlng,
          Math.max(this.map.getZoom(), this.zoomOnClick)
        );
      }

    },
    createLine(e) {
      const polyline = L.polyline(
        e.latlngs, 
        { 
          color:  e.properties && e.properties.color || 'blue',
          weight: e.properties && e.properties.weight || 5
        }
      ).addTo(this.map);
      this.polyline = polyline
      // Zoom sur la ligne
      this.map.fitBounds(polyline.getBounds());
    },
    focusOnMarker() {
      if (this.marker && this.marker._latlng) {
        this.map.flyTo(
          this.marker._latlng,
          Math.max(this.map.getZoom(), this.zoomOnClick),
          { duration: 1 }
        );
      }
    },
    focusOn(geolocation, type, index) {
      if (type === 'mission') {
        if (this.pathGeolocation) {
          index += 1;
        }
      }
      if (geolocation && geolocation.coordinates) {
        let toFocus = {
          lat: geolocation.coordinates[1],
          lng: geolocation.coordinates[0],
        };
        this.map.flyTo(toFocus, this.zoomOnClick + 1, {
          animate: true,
          duration: 1.5,
        });
        if (!this.pathGeolocation || index !== 0) {
          setTimeout(() => this.markers[index].openPopup(), 1200);
        }
      }
    },
    locateUser() {
      this.map.locate({ setView: true });
    },
    addMarkers(markersInformation) {
      if (this.marker) {
        this.map.removeLayer(this.marker);
      }
      let newMarker;
      if (markersInformation.find((marker) => marker.type === 'flag')) {
        const marker = markersInformation.find(
          (marker) => marker.type === 'flag'
        );
        newMarker = new L.Marker(marker.coordinates)
          .addTo(this.map)
          .bindPopup(marker.name, { closeButton: true });
        this.$set(this.markers, marker.name, newMarker);
      } else {
        markersInformation.forEach((marker) => {
          //this.addMarker(markerInformation)
          newMarker = new L.Circle(marker.coordinates, {
            radius: marker.radius,
            fillColor: marker.fillColor,
            color: marker.color,
            weight: 4,
            fillOpacity: marker.fillOpacity,
          })
            .addTo(this.map)
            .bindPopup(marker.name, { closeButton: true });
          this.$set(this.markers, marker.name, newMarker);
          if (marker.type === 'player') this.drawLine(marker);
        });
      }
    },
    drawLine(marker) {
      let latlngs = [];
      //Get latlng from  marker, add twice to create dot
      latlngs.push(this.markers[marker.name].getLatLng());
      latlngs.push(this.markers[marker.name].getLatLng());
      // create a polyline from an arrays of LatLng points
      var polyline = L.polyline(latlngs, { color: marker.color }).addTo(
        this.map
      );
    },
    submit() {
      this.$emit('submit', this.site);
    },

    cancel() {
      this.$emit('cancel');
      //setTimeout(() => this.clear(), 1000);
    },

    deleteLocation() {
      this.map.removeLayer(this.marker);
      this.marker = null;
      this.$emit('delete-location');
    },
    clear() {
      //efface toutes les données sur la carte

      if (this.markers.length > 0) {
        this.markers.forEach((marker) => {
          this.map.removeLayer(marker);
        });
      }

      if (this.marker) {
        this.map.removeLayer(this.marker);
      }

      this.markers = [];
      this.missionsActive = [];
      this.marker = null;
    },

    changeLayer(layer) {
      //change le fond de carte
      this.selectedUrl = layer.url;
      this.attribution = layer.attribution;
    },
  },
};
</script>

<style scoped lang="sass">

.banner
  position: absolute
  top : 50%
  left: 50%
  transform: translate(-50%, -50%);
  width: 100%;
  color: rgba(0,0,0,0.8)
  text-align: center
  text-justify: center
  z-index: 4000

.legendTitle
  font-size: medium
  text-align: center
  text-justify: justify
  margin-top: 10px

.legendText
  max-width: 60px
  max-height: 30px
  text-align: center
  text-justify: center
  line-height: 120%

.legendTextlarge
  width: 80px
  max-height: 30px
  text-align: center
  text-justify: center
  line-height: 120%

.locationMenu
  max-width: 20%
  background-color: #F7F7F7


.layerIcon
  height: 25px
  width: 25px
  cursor: pointer

.optionsCard
  min-height: 110px
  width: 100px

.legendCard
  min-height: 20px
  width: 100px

.legendCardBar
  min-height: 20px
  max-height: 380px
  overflow: auto
  width: 200px

.locationFindIcon
  height: 25px
  width: 25px
  cursor: pointer

.leaflet-popup-content-wrapper
  background-color: red

.switchHistory
  margin-top: 0px !important
  margin-bottom: 0px !important
</style>
